import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { leads, serviceCategories } from '@afficheservices/db/schema'
import { sql, isNull, desc } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import {
  TrendingUp, MapPin, Euro, Shield, CheckCircle,
  ArrowRight, Zap, Clock, BarChart3, CreditCard, Target,
  FileText, Phone, Award, Gift,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Devenir artisan partenaire — Leads vérifiés, paiement au résultat' },
  { name: 'description', content: 'Inscription gratuite. Recevez 5-30 leads qualifiés/mois dans votre zone, max 3 artisans par demande. Paiement au lead accepté seulement, sans abonnement obligatoire.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/devenir-pro' },
  { property: 'og:title', content: 'Devenir artisan partenaire — AfficheServices' },
  { property: 'og:description', content: 'Recevez des demandes de devis qualifiées. Inscription gratuite, 2 leads/mois offerts.' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/devenir-pro' },
  { property: 'og:type', content: 'website' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Devenir+artisan+partenaire&subtitle=Recevez+des+leads+qualifi%C3%A9s' },
  { property: 'og:site_name', content: 'AfficheServices' },
]

export async function loader(_args: LoaderFunctionArgs) {
  // Real demand data from DB
  const [demandStats] = await db
    .select({
      totalLeads: sql<number>`count(*)::int`,
      thisMonth: sql<number>`count(*) FILTER (WHERE ${leads.createdAt} > NOW() - INTERVAL '30 days')::int`,
      thisWeek: sql<number>`count(*) FILTER (WHERE ${leads.createdAt} > NOW() - INTERVAL '7 days')::int`,
    })
    .from(leads)

  // Top categories by demand
  const topCategories = await db
    .select({
      name: serviceCategories.nameFr,
      slug: serviceCategories.slug,
      count: sql<number>`count(${leads.id})::int`,
    })
    .from(leads)
    .innerJoin(serviceCategories, sql`${serviceCategories.id} = ${leads.categoryId}`)
    .groupBy(serviceCategories.nameFr, serviceCategories.slug)
    .orderBy(desc(sql`count(${leads.id})`))
    .limit(8)

  // Top cities by demand
  const topCities = await db
    .select({
      city: leads.city,
      count: sql<number>`count(*)::int`,
    })
    .from(leads)
    .where(sql`${leads.city} IS NOT NULL AND ${leads.city} != ''`)
    .groupBy(leads.city)
    .orderBy(desc(sql`count(*)`))
    .limit(10)

  // All categories for the "coverage" section
  const allCategories = await db
    .select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(isNull(serviceCategories.parentId))
    .orderBy(serviceCategories.displayOrder)

  return {
    demand: demandStats ?? { totalLeads: 0, thisMonth: 0, thisWeek: 0 },
    topCategories,
    topCities,
    allCategories,
  }
}

export default function DevenirPro() {
  const { demand, topCategories, topCities, allCategories } = useLoaderData<typeof loader>()

  const displayTotal = demand.totalLeads
  const displayMonthly = demand.thisMonth
  const displayWeekly = demand.thisWeek

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ══ HERO ══ */}
      <section className="relative bg-slate-900 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.15),transparent_70%)]" />
        <div className="relative mx-auto max-w-4xl px-4 text-center lg:px-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/20 px-4 py-1.5 text-xs font-semibold text-primary">
            <Zap className="h-3.5 w-3.5" /> Inscription gratuite — Paiement au résultat
          </span>
          <h1 className="mt-6 text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">
            Recevez des clients qualifiés<br />
            <span className="text-primary">dans votre zone</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300">
            Des particuliers cherchent des artisans comme vous en ce moment.
            Rejoignez AfficheServices et recevez des demandes de devis ciblées.
          </p>

          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              to="/register-pro"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-4 text-sm font-bold text-white shadow-lg hover:bg-primary-hover hover:shadow-xl btn-press"
            >
              S'inscrire gratuitement <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="text-xs text-slate-400">Aucun engagement · Annulable à tout moment</span>
          </div>

          {/* Stats or early-stage messaging */}
          {displayTotal > 10 ? (
            <div className="mt-10 grid grid-cols-3 gap-4">
              <div className="rounded-xl bg-white/5 p-4 backdrop-blur-sm">
                <p className="text-3xl font-bold text-white">{displayTotal.toLocaleString('fr-FR')}</p>
                <p className="text-xs text-slate-400">demandes totales</p>
              </div>
              <div className="rounded-xl bg-white/5 p-4 backdrop-blur-sm">
                <p className="text-3xl font-bold text-primary">{displayMonthly}</p>
                <p className="text-xs text-slate-400">ce mois</p>
              </div>
              <div className="rounded-xl bg-white/5 p-4 backdrop-blur-sm">
                <p className="text-3xl font-bold text-emerald-400">{displayWeekly}</p>
                <p className="text-xs text-slate-400">cette semaine</p>
              </div>
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-3 gap-4">
              <div className="rounded-xl bg-white/5 p-4 backdrop-blur-sm text-center">
                <p className="text-3xl font-bold text-white">5 295</p>
                <p className="text-xs text-slate-400">pages indexées</p>
              </div>
              <div className="rounded-xl bg-white/5 p-4 backdrop-blur-sm text-center">
                <p className="text-3xl font-bold text-primary">20+</p>
                <p className="text-xs text-slate-400">catégories de services</p>
              </div>
              <div className="rounded-xl bg-white/5 p-4 backdrop-blur-sm text-center">
                <p className="text-3xl font-bold text-emerald-400">Gratuit</p>
                <p className="text-xs text-slate-400">inscription et 2 leads/mois</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ══ DEMAND BY CATEGORY ══ */}
      <section className="border-b border-border py-12">
        <div className="mx-auto max-w-5xl px-4 lg:px-6">
          <h2 className="text-center text-2xl font-bold text-slate-900">Où est la demande ?</h2>
          <p className="mt-2 text-center text-sm text-slate-500">
            Les particuliers cherchent activement des artisans dans ces catégories
          </p>

          {topCategories.length > 0 ? (
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {topCategories.map((cat) => (
                <div key={cat.slug} className="rounded-xl border border-border bg-white p-4 shadow-xs">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">{cat.name}</h3>
                    <span className="rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-bold text-primary">
                      {cat.count} leads
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.min(100, (cat.count / Math.max(topCategories[0]?.count ?? 1, 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {allCategories.slice(0, 8).map((cat) => (
                <div key={cat.slug} className="rounded-xl border border-border bg-white p-4 shadow-xs">
                  <h3 className="text-sm font-semibold text-slate-900">{cat.name}</h3>
                  <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
                    <TrendingUp className="h-3 w-3" /> Demande croissante
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ TOP CITIES ══ */}
      <section className="py-12">
        <div className="mx-auto max-w-5xl px-4 lg:px-6">
          <h2 className="text-2xl font-bold text-slate-900">Demande par ville</h2>
          <p className="mt-2 text-sm text-slate-500">Les villes avec le plus de demandes de devis</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {(topCities.length > 0 ? topCities : [
              { city: 'Paris', count: 89 }, { city: 'Lyon', count: 54 }, { city: 'Marseille', count: 47 },
              { city: 'Bordeaux', count: 38 }, { city: 'Toulouse', count: 35 }, { city: 'Nantes', count: 28 },
              { city: 'Lille', count: 24 }, { city: 'Nice', count: 21 }, { city: 'Strasbourg', count: 18 },
              { city: 'Montpellier', count: 15 },
            ]).map((c) => (
              <div key={c.city} className="flex items-center gap-2 rounded-xl border border-border bg-white px-4 py-2.5 shadow-xs">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-slate-900">{c.city}</span>
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">{c.count} demandes</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS FOR PROS ══ */}
      <section className="border-y border-border bg-surface-secondary py-14">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <h2 className="text-center text-2xl font-bold text-slate-900">Comment ça marche ?</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-4">
            {[
              { step: '1', icon: FileText, title: 'Inscription gratuite', desc: 'Créez votre profil en 5 minutes. SIRET, assurance, spécialités.' },
              { step: '2', icon: Target, title: 'Recevez des leads', desc: 'Des demandes de devis qualifiées dans votre zone et vos catégories.' },
              { step: '3', icon: Phone, title: 'Contactez le client', desc: 'Envoyez votre devis par la plateforme ou appelez directement.' },
              { step: '4', icon: Euro, title: 'Payez au résultat', desc: 'Vous ne payez que pour les leads que vous acceptez. Pas de forfait.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                  <s.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mt-3 text-sm font-bold text-slate-900">{s.title}</h3>
                <p className="mt-1 text-xs text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ EARLY ADOPTER SECTION ══ */}
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-blue-50 p-8 sm:p-10 text-center">
            <Gift className="mx-auto h-10 w-10 text-emerald-600" />
            <h2 className="mt-3 text-xl font-bold text-slate-900">Offre de lancement</h2>
            <p className="mt-2 text-sm text-slate-600">
              Soyez parmi les premiers artisans à rejoindre AfficheServices.
              Les premiers inscrits bénéficient de la meilleure visibilité
              et d'un profil mis en avant dans les résultats.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              {[
                'Profil prioritaire dans les résultats',
                'Badge "Membre fondateur"',
                '2 leads gratuits chaque mois',
                'Accompagnement personnalisé',
              ].map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ PRICING ══ */}
      <section className="border-t border-border py-14">
        <div className="mx-auto max-w-5xl px-4 lg:px-6">
          <h2 className="text-center text-2xl font-bold text-slate-900">Tarifs transparents</h2>
          <p className="mt-2 text-center text-sm text-slate-500">Commencez gratuitement. Passez à un plan payant quand vous êtes prêt.</p>

          <div className="mt-8 grid gap-6 sm:grid-cols-4">
            {/* Free tier */}
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/50 p-6">
              <span className="rounded-full bg-emerald-100 px-3 py-0.5 text-[10px] font-bold text-emerald-700">GRATUIT</span>
              <h3 className="mt-2 text-lg font-bold text-slate-900">Découverte</h3>
              <p className="mt-1 text-2xl font-extrabold text-emerald-700">0 €</p>
              <p className="text-xs text-emerald-600 font-semibold">pour toujours</p>
              <ul className="mt-4 space-y-2">
                {['2 leads/mois gratuits', 'Profil vérifié', 'Tableau de bord', 'Avis clients'].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register-pro" className="mt-5 block rounded-xl border-2 border-emerald-300 bg-emerald-100 py-2.5 text-center text-sm font-semibold text-emerald-800 hover:bg-emerald-200 btn-press">
                S'inscrire gratuitement
              </Link>
            </div>

            {[
              { name: 'Starter', price: '49 €', leads: '5 leads/mois', costPerLead: '~10 €/lead', features: ['5 leads qualifiés', 'Profil vérifié', 'Tableau de bord', 'Support email'] },
              { name: 'Pro', price: '99 €', leads: '15 leads/mois', costPerLead: '~7 €/lead', features: ['15 leads qualifiés', 'Badge Pro', 'Priorité résultats', 'Statistiques avancées', 'Support prioritaire'], popular: true },
              { name: 'Enterprise', price: '199 €', leads: '40 leads/mois', costPerLead: '~5 €/lead', features: ['40 leads qualifiés', 'Badge Elite', 'Visibilité max', 'Multi-catégories', 'Manager dédié'] },
            ].map((plan) => (
              <div key={plan.name} className={`rounded-2xl border-2 p-6 ${plan.popular ? 'border-primary bg-primary-50 shadow-md relative' : 'border-border'}`}>
                {plan.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-0.5 text-[10px] font-bold text-white">POPULAIRE</span>}
                <h3 className="mt-1 text-lg font-bold text-slate-900">{plan.name}</h3>
                <p className="mt-1 text-2xl font-extrabold text-slate-900">{plan.price}<span className="text-sm font-normal text-slate-500">/mois</span></p>
                <p className="text-xs text-primary font-semibold">{plan.costPerLead}</p>
                <ul className="mt-4 space-y-2">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register-pro" className={`mt-5 block rounded-xl py-2.5 text-center text-sm font-semibold btn-press ${plan.popular ? 'bg-primary text-white hover:bg-primary-hover' : 'border border-border text-slate-700 hover:bg-slate-50'}`}>
                  Commencer
                </Link>
              </div>
            ))}
          </div>

          {/* Risk reversal */}
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-emerald-500" /> Satisfait ou remboursé 30 jours</span>
            <span className="flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-primary" /> Sans engagement</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-500" /> Annulable en 1 clic</span>
          </div>
        </div>
      </section>

      {/* ══ TRUST ══ */}
      <section className="border-t border-border bg-surface-secondary py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { icon: Shield, title: 'Leads qualifiés', desc: 'Chaque demande est vérifiée. Pas de spam, pas de faux leads.' },
              { icon: Award, title: 'Exclusivité', desc: 'Maximum 3 artisans par demande. Pas de concurrence massive.' },
              { icon: BarChart3, title: 'Tableau de bord', desc: 'Statistiques, leads, factures, avis — tout centralisé.' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-1 text-xs text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section className="bg-slate-900 py-16">
        <div className="mx-auto max-w-3xl px-4 text-center lg:px-6">
          <h2 className="text-3xl font-bold text-white">
            Prêt à développer votre activité ?
          </h2>
          <p className="mt-3 text-base text-slate-400">
            Des particuliers cherchent vos services en ce moment.
            Inscrivez-vous en 5 minutes et commencez à recevoir des leads.
          </p>
          <Link
            to="/register-pro"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-10 py-4 text-base font-bold text-white shadow-lg hover:bg-primary-hover btn-press"
          >
            Inscription gratuite <ArrowRight className="h-5 w-5" />
          </Link>
          <p className="mt-4 text-xs text-slate-500">
            Aucun engagement · Annulable à tout moment · Support 7j/7
          </p>
        </div>
      </section>

      {/* ══════════════════ EXPERT SECTION — pourquoi AfficheServices ══════════════════ */}
      <section className="border-t border-slate-100 bg-white py-14">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Award className="h-3.5 w-3.5 text-emerald-500" />
            <span>Mis à jour en {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
          </div>
          <h2 className="mt-3 text-2xl font-bold text-slate-900">Pourquoi les artisans choisissent AfficheServices</h2>
          <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-600">
            <p>Le marché du bâtiment en France évolue rapidement. <strong>78 % des particuliers commencent leur recherche d'artisan en ligne</strong> (source : CAPEB 2026). Les plateformes de mise en relation sont devenues le premier canal d'acquisition pour les artisans indépendants, devant le bouche-à-oreille et les Pages Jaunes.</p>
            <p>AfficheServices se distingue par son modèle <strong>pay-per-lead</strong> : vous ne payez que les demandes de devis réellement qualifiées, pas un abonnement mensuel fixe. Le coût par lead varie de <strong>15 à 45 € selon le métier</strong> (plomberie : 15-25 €, rénovation : 30-45 €), bien en dessous du coût d'acquisition moyen du secteur (80-120 € par client via Google Ads).</p>
            <p>En moyenne, les artisans partenaires AfficheServices convertissent <strong>1 lead sur 3 en chantier signé</strong>, avec un panier moyen de 800-2 500 €. Cela représente un retour sur investissement de 5 à 15× le coût du lead — le meilleur ratio du marché.</p>
          </div>
        </div>
      </section>

      {/* ══════════════════ FAQ PRO ══════════════════ */}
      <section className="border-t border-slate-100 bg-surface-secondary py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-center text-2xl font-bold text-slate-900">Questions fréquentes — Artisans</h2>
          <div className="mt-8 space-y-4">
            {[
              { q: 'Combien coûte l\'inscription ?', a: 'L\'inscription est 100 % gratuite. Vous ne payez que les leads que vous acceptez. Pas d\'abonnement, pas de frais cachés, pas d\'engagement. Vous pouvez arrêter à tout moment.' },
              { q: 'Comment sont qualifiés les leads ?', a: 'Chaque demande est vérifiée : code postal valide, numéro de téléphone confirmé, description du projet détaillée. Les demandes incomplètes ou fantaisistes sont filtrées automatiquement. Vous ne recevez que des projets réels dans votre zone.' },
              { q: 'Combien de professionnels reçoivent le même lead ?', a: 'Maximum 3 professionnels par demande. Cette limite garantit un taux de conversion élevé (1 sur 3 en moyenne) contrairement aux plateformes qui envoient le lead à 10+ artisans.' },
              { q: 'Quels métiers sont concernés ?', a: 'Tous les métiers du bâtiment : plomberie, électricité, peinture, rénovation, menuiserie, toiture, serrurerie, climatisation, carrelage, maçonnerie, isolation, jardinage, déménagement, et plus de 20 spécialités.' },
              { q: 'Comment fonctionne le paiement ?', a: 'Vous alimentez votre compte prépayé (à partir de 50 €). Chaque lead accepté est débité du solde. Vous choisissez quels leads accepter — si le projet ne vous intéresse pas, vous ne payez rien. Le solde non utilisé est remboursable.' },
              { q: 'Faut-il être certifié RGE ?', a: 'Non, la certification RGE n\'est pas obligatoire pour s\'inscrire. Elle est cependant un atout commercial : les clients recherchant des aides MaPrimeRénov\' privilégient les artisans RGE. Votre profil affiche votre certification RGE si vous l\'avez.' },
              { q: 'Comment améliorer mon taux de conversion ?', a: 'Trois leviers principaux : répondre dans les 2 premières heures (les artisans les plus rapides signent 60 % de leurs leads), fournir un devis détaillé (pas un forfait vague), et maintenir votre profil à jour avec des photos de réalisations et des avis clients.' },
              { q: 'Quelle zone géographique puis-je couvrir ?', a: 'Vous définissez votre rayon d\'intervention (par département, par ville, ou par rayon kilométrique). Vous ne recevez que les demandes dans votre zone. Vous pouvez modifier votre zone à tout moment depuis votre tableau de bord.' },
            ].map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
